import { useEffect } from 'react';
import { clearGoogleOTPData } from '@/utils/googleOTPData';

interface GoogleOTPData {
  subscription_type: string;
  product_slug: string;
}

const GOOGLE_OTP_STORAGE_KEY = 'google_otp_data';

const getStoredOTPData = (): GoogleOTPData | null => {
  const storedData = localStorage.getItem(GOOGLE_OTP_STORAGE_KEY);
  if (!storedData) return null;

  try {
    return JSON.parse(storedData);
  } catch {
    localStorage.removeItem(GOOGLE_OTP_STORAGE_KEY);
    return null;
  }
};

const storeOTPData = (productSlug: string): void => {
  const otpData: GoogleOTPData = {
    subscription_type: 'otp',
    product_slug: productSlug,
  };
  localStorage.setItem(GOOGLE_OTP_STORAGE_KEY, JSON.stringify(otpData));
};

const clearOTPData = (): void => {
  localStorage.removeItem(GOOGLE_OTP_STORAGE_KEY);
};

/**
 * Manages Google OTP subscription data in localStorage based on URL params.
 * Params are preserved through interstitial navigation.
 */
const useGoogleOTPFlow = (subscriptionType: string | null, productSlug?: string | null) => {
  useEffect(() => {
    const storedData = getStoredOTPData();

    if (storedData && productSlug && storedData.product_slug !== productSlug) {
      clearOTPData();
      return;
    }

    if (subscriptionType === 'otp' && productSlug) {
      storeOTPData(productSlug);
    } else if (!subscriptionType) {
      clearOTPData();
    }
  }, [subscriptionType, productSlug]);

  return {
    clearSubscriptionType: clearGoogleOTPData,
  };
};

export default useGoogleOTPFlow;
